<template>
  <div class="live-acquisition-data-chart">
    <div class="title">
      Hourly Registrations, FTDs & FTD Conversion Rate
      <q-select
        class="select"
        v-model="selectedFilter"
        :options="options"
        virtual-scroll-slice-size="30"
        virtual-scroll-item-size="30"
        popup-content-class="select__options"
        dense
        options-dense
        borderless
        color="secondary"
        @input="fetchData()"
      >
        <template v-slot:option="scope">
          <q-item
            v-bind="scope.itemProps"
            v-on="scope.itemEvents"
            class="select__options--item"
          >
            <q-item-section>
              <q-item-label v-html="scope.opt.label" />
            </q-item-section>
          </q-item>
        </template>
      </q-select>
    </div>
    <div class="live-acquisition-data-chart__content" />
    <div class="live-acquisition-data-chart__legend" />
    <q-inner-loading :showing="visible">
      <q-spinner size="50px" color="secondary" />
    </q-inner-loading>
    <q-resize-observer :debounce="500" @resize="refreshChart" />
  </div>
</template>

<script>
import bb, { bar, spline } from "billboard.js";
import "billboard.js/dist/theme/insight.css";
import * as d3 from "d3";
import { formatPropertyLabel } from "@/utils/formatText";
import { count } from "@/utils/formatNumbers";
import { formatPercentage } from "@/utils/formatPercentage";

export default {
  name: "LiveAcquisitionChart",
  props: {
    dataset: String,
    filter: Object,
  },
  data() {
    return {
      visible: true,
      interval: null,
      chart: "",
      chartData: null,
      selectedFilter: { label: "All", value: "All" },
      options: [
        {
          label: "All",
          value: "All",
        },
        {
          label: "Affiliate",
          value: "Affiliate",
        },
        {
          label: "App",
          value: "App",
        },
        {
          label: "Apple Search Ads",
          value: "Apple",
        },
        {
          label: "Facebook",
          value: "Facebook",
        },
        {
          label: "Internal Cross Sell",
          value: "Internal_Cross_Sell",
        },
        {
          label: "IOS Native App",
          value: "IOS",
        },
        {
          label: "Android Organic",
          value: "Android",
        },
        {
          label: "Paid Search",
          value: "Paid_Search",
        },

        {
          label: "Unknown",
          value: "Unknown",
        },
      ],
    };
  },

  methods: {
    async fetchData(siteId, networkId, operatorId) {
      // Dataset name is the store procedure name used to return the results.
      const payload = {
        filterOptions: {
          siteIds: siteId ? [siteId] : [],
          networkId,
          operatorId,
        },
        report: "live-acquisition",
        dataset: this.$props.dataset.replace(/ /g, ""),
      };

      await this.$store
        .dispatch("fetchData", payload)
        .then((response) => {
          this.chartData = response.data;
          this.visible = false;
        })
        .catch((error) => {
          this.errors.push(error);
        });
      if (this.selectedFilter) {
        let type = this.selectedFilter.value;

        this.chart = bb.generate({
          bindto: ".live-acquisition-data-chart__content",
          size: {
            height: 280,
          },
          padding: {
            top: 10,
            right: 70,
            bottom: 0,
            left: 60,
          },
          // Data Assignment
          data: {
            json: this.chartData,
            xFormat: "%H:%M:%S",
            keys: {
              x: "HourName",
              value: [
                `${type}_Registrations`,
                `${type}_FTDs`,
                `${type}_FTD_Conversion_Rate`,
              ],
            },
            groups: [[`${type}_Registrations`, `${type}_FTDs`]],
            types: {
              All_FTD_Conversion_Rate: spline(),
              All_Registrations: bar(),
              All_FTDs: bar(),
              Affiliate_FTD_Conversion_Rate: spline(),
              Affiliate_Registrations: bar(),
              Affiliate_FTDs: bar(),
              App_FTD_Conversion_Rate: spline(),
              App_Registrations: bar(),
              App_FTDs: bar(),
              Apple_FTD_Converstion_Rate: spline(),
              Apple_Registrations: bar(),
              Apple_FTDS: bar(),
              Facebook_FTD_Conversion_Rate: spline(),
              Facebook_Registrations: bar(),
              Facebook_FTDs: bar(),
              Internal_Cross_Sell_FTD_Conversion_Rate: spline(),
              Internal_Cross_Sell_Registrations: bar(),
              Internal_Cross_Sell_FTDs: bar(),
              IOS_FTD_Conversion_Rate: spline(),
              IOS_Registrations: bar(),
              IOS_FTDs: bar(),
              Android_FTD_Conversion_Rate: spline(),
              Android_Registrations: bar(),
              Android_FTDs: bar(),
              Paid_Search_FTD_Conversion_Rate: spline(),
              Paid_Search_Registrations: bar(),
              Paid_Search_FTDs: bar(),
              Unknown_FTD_Conversion_Rate: spline(),
              Unknown_Registrations: bar(),
              Unknown_FTDs: bar(),
            },
            axes: {
              All_FTD_Conversion_Rate: "y2",
              All_Registrations: "y",
              All_FTDs: "y",
              Affiliate_FTD_Conversion_Rate: "y2",
              Affiliate_Registrations: "y",
              Affiliate_FTDs: "y",
              App_FTD_Conversion_Rate: "y2",
              App_Registrations: "y",
              App_FTDs: "y",
              Apple_FTD_Conversion_Rate: "y2",
              Apple_Registrations: "y",
              Apple_FTDS: "y",
              Facebook_FTD_Conversion_Rate: "y2",
              Facebook_Registrations: "y",
              Facebook_FTDs: "y",
              Internal_Cross_Sell_FTD_Conversion_Rate: "y2",
              Internal_Cross_Sell_Registrations: "y",
              Internal_Cross_Sell_FTDs: "y",
              IOS_FTD_Conversion_Rate: "y2",
              IOS_Registrations: "y",
              IOS_FTDs: "y",
              Android_FTD_Conversion_Rate: "y2",
              Android_Registrations: "y",
              Android_FTDs: "y",
              Paid_Search_FTD_Conversion_Rate: "y2",
              Paid_Search_Registrations: "y",
              Paid_Search_FTDs: "y",
              Unknown_FTD_Conversion_Rate: "y2",
              Unknown_Registrations: "y",
              Unknown_FTDs: "y",
            },
            colors: {
              All_FTD_Conversion_Rate: "#FD7496",
              All_Registrations: "#458EDD",
              All_FTDs: "#122389",
              Affiliate_FTD_Conversion_Rate: "#FD7496",
              Affiliate_Registrations: "#458EDD",
              Affiliate_FTDs: "#122389",
              App_FTD_Conversion_Rate: "#FD7496",
              App_Registrations: "#458EDD",
              App_FTDs: "#122389",
              Apple_FTD_Conversion_Rate: "#FD7496",
              Apple_Registrations: "#458EDD",
              Apple_FTDs: "#122389",
              Facebook_FTD_Conversion_Rate: "#FD7496",
              Facebook_Registrations: "#458EDD",
              Facebook_FTDs: "#122389",
              Internal_Cross_Sell_FTD_Conversion_Rate: "#FD7496",
              Internal_Cross_Sell_Registrations: "#458EDD",
              Internal_Cross_Sell_FTDs: "#122389",
              IOS_FTD_Conversion_Rate: "#FD7496",
              IOS_Registrations: "#458EDD",
              IOS_FTDs: "#122389",
              Android_FTD_Conversion_Rate: "#FD7496",
              Android_Registrations: "#458EDD",
              Android_FTDs: "#122389",
              Paid_Search_FTD_Conversion_Rate: "#FD7496",
              Paid_Search_Registrations: "#458EDD",
              Paid_Search_FTDs: "#122389",
              Unknown_FTD_Conversion_Rate: "#FD7496",
              Unknown_Registrations: "#458EDD",
              Unknown_FTDs: "#122389",
            },
            // Return data based on x-axis click event
            onclick: (d) => {
              let selectedTime = d3.timeFormat("%H:%M:%S")(d.x);
              this.$store.commit("SET_LIVE_ACQUISITION_FILTER_TIME", {
                time: selectedTime,
              });
            },
          },
          // Pointer Control
          point: {
            focus: {
              only: true,
            },
          },
          // Chart Display
          bar: {
            padding: 2,
            width: {
              max: 20,
            },
            radius: {
              ratio: 0.1,
            },
          },
          spline: {
            padding: 0,
            size: 5,
          },
          // Axis Display
          axis: {
            x: {
              type: "timeseries",
              tick: {
                format: "%H:%M:%S",
                fit: true,
                show: true,
                culling: {
                  max: 8,
                },
              },
              text: {
                show: true,
              },
            },
            y: {
              tick: {
                show: false,
                culling: {
                  max: 5,
                },
                text: {
                  show: true,
                },
              },
              label: {
                text: "Hourly Registrations & FTDs",
                position: "outer-middle",
              },
            },
            y2: {
              show: true,
              tick: {
                show: false,
                format: function(value) {
                  return value + "%";
                },
                culling: {
                  max: 5,
                },
                text: {
                  show: true,
                },
              },
              label: {
                text: "FTD Conversion Rate",
                position: "outer-middle",
              },
            },
          },
          // Additional Lines
          grid: {
            y: {
              show: true,
            },
          },
          // Legend Display
          legend: {
            contents: {
              bindto: ".live-acquisition-data-chart__legend",
              template: (title, color) => {
                return title === `${type}_FTD_Conversion_Rate`
                  ? "<div class='live-acquisition-data-chart__legend--category'><span class='legend-item-line' style='background-color:" +
                      color +
                      ";'></span>" +
                      title.replace(/_/g, " ").trim() +
                      "</div>"
                  : "<div class='live-acquisition-data-chart__legend--category'><span class='legend-item' style='background-color:" +
                      color +
                      ";'></span>" +
                      title.replace(/_/g, " ").trim() +
                      "</div>";
              },
            },
          },
          // Tooltip Display
          tooltip: {
            format: {
              title: function(datetime) {
                return d3.timeFormat("%H:%M:%S")(datetime);
              },
              name: (key) => {
                return formatPropertyLabel(key);
              },
              value: (key, id, ratio) => {
                if (ratio == `${type}_FTD_Conversion_Rate`) {
                  return formatPercentage(key);
                } else {
                  return count(key);
                }
              },
            },
          },
          clipPath: true,
        });
      }
    },
    // Resize the chart when the width of the container is changed. I.e menu open then closed.
    refreshChart() {
      if (this.chart) {
        setTimeout(() => {
          try {
            this.chart.resize();
          } catch {
            // Prevents error occuring when container is refreshed before chart object is reached
          }
        }, 100);
      }
    },
  },
  watch: {
    filter: function(filterOption) {
      this.fetchData(
        filterOption.siteId,
        filterOption.networkId,
        filterOption.operatorId
      );
      this.visible = true;

      clearInterval(this.interval);
      this.interval = setInterval(() => {
        this.fetchData(
          filterOption.siteId,
          filterOption.networkId,
          filterOption.operatorId
        );
      }, 180000);
    },
  },

  async mounted() {
    this.fetchData(
      this.$store.state.siteFilter.id,
      this.$store.state.networkFilter.id,
      this.$store.state.selectedOperator.id
    );
    this.visible = true;
    clearInterval(this.interval);
    this.interval = setInterval(() => {
      this.fetchData(
        this.$store.state.siteFilter.id,
        this.$store.state.networkFilter.id,
        this.$store.state.selectedOperator.id
      );
    }, 180000);
  },

  // Prevent Memory Leak
  beforeDestroy() {
    clearInterval(this.interval);
  },
};
</script>
<style lang="scss">
.live-acquisition-data-chart {
  overflow: hidden;
  &__content {
    svg {
      margin: auto;
      .domain {
        display: none;
      }
      .bb-axis-y text,
      .bb-axis-y2 text {
        fill: #4c5862;
        font-family: Barlow;
        font-weight: normal;
        font-size: 12px;
        line-height: 1;
      }
      .bb-line {
        stroke-width: 4px;
      }
      .bb-xgrid-focus line {
        stroke: #c2cfe0;
        stroke-width: 1px;
      }
      .bb-grid line {
        stroke: #e5e5e5;
      }
      .pointer {
        &__outer {
          stroke: none;
          fill: #ffa000;
          opacity: 0.2;
        }
        &__middle {
          stroke: none;
          fill: #ffa000;
          opacity: 0.4;
        }
        &__inner {
          stroke: #ffa000;
          stroke-width: 1;
          fill: #fff;
        }
      }
      .bb-event-rects .bb-event-rect {
        cursor: pointer;
      }
    }

    .bb-tooltip {
      border-collapse: separate;
      border-spacing: 0;
      empty-cells: show;
      border: 0;
      background: rgba(0, 0, 0, 0.5);
      backdrop-filter: blur(50px);
      border-radius: 5px;
      text-align: left;
      font-size: 11px;
      overflow: hidden;
      tr {
        font-family: Barlow;
        font-style: normal;
        font-weight: 600;
        font-size: 12.7397px;
        th {
          font-size: 12px;
          padding: 4px 8px;
          text-align: left;
          border-bottom: 0;
          color: white;
        }

        .name {
          text-align: left;
          background-color: transparent;
          color: white;
        }
        .value {
          text-align: right;
          background-color: transparent;
          color: white;
        }
      }
    }
  }
  &__legend {
    display: flex;
    justify-content: flex-end;
    position: relative;
    bottom: 15px;
    margin: 0 20px;
    font-family: Barlow;
    font-style: normal;
    font-weight: normal;
    font-size: 12px;
    z-index: 1;
    &--category {
      display: flex;
      flex-direction: row;
      align-items: center;
      margin-right: 20px;
      .legend-item {
        display: block;
        height: 15px;
        width: 15px;
        border-radius: 50%;
        margin-right: 8px;
      }
      .legend-item-line {
        display: block;
        height: 3px;
        width: 20px;
        margin-right: 8px;
      }
    }
  }
}
// Dark Mode
.body {
  &--dark {
    .live-acquisition-data-chart {
      &__content {
        svg {
          .bb-axis-y text,
          .bb-axis-y2 text {
            fill: #fff;
          }
          .bb-xgrid-focus line {
            stroke: #c2cfe0;
          }
          .bb-grid line {
            stroke: #353535;
          }
        }
        .bb-tooltip {
          background: rgba(255, 255, 255, 0.8);
          backdrop-filter: blur(50px);
          tr {
            th {
              color: black;
            }
            .name {
              color: black;
            }
            .value {
              color: black;
            }
          }
        }
      }
    }
  }
}
</style>
